import type { ThreadMessageLike } from "@assistant-ui/react";

/** Key under `metadata.custom` holding the backend message id (written by history + transport). */
export const MESSAGE_BACKEND_ID_KEY = "backendId";

/**
 * Key under `metadata.custom` holding the raw sub-agent names a live turn routed to. Written only
 * by the streaming transport, so its absence means the message was loaded from history.
 */
export const MESSAGE_SUB_AGENTS_KEY = "subAgents";

/**
 * Key under `metadata.custom` holding the welcome card a user message came from (the card's
 * `id`, e.g. `messaging:0`). Set only by the welcome screen; the transport sends it as the
 * `suggestion_id` hint so the agent can skip its routing call. Typed messages never carry it.
 */
export const MESSAGE_SUGGESTION_ID_KEY = "suggestionId";

interface MetadataCarrier {
  metadata?: Pick<NonNullable<ThreadMessageLike["metadata"]>, "custom">;
}

/** Backend message id recorded in custom metadata, or undefined when unknown. */
export function getBackendMessageId(
  message: MetadataCarrier | undefined | null,
): string | undefined {
  const id = message?.metadata?.custom?.[MESSAGE_BACKEND_ID_KEY];
  return typeof id === "string" && id ? id : undefined;
}

/**
 * Sub-agents recorded on a live-streamed message (possibly empty), or undefined for a message
 * loaded from history, which carries no routing info.
 */
export function getMessageSubAgents(
  message: MetadataCarrier | undefined | null,
): string[] | undefined {
  const value = message?.metadata?.custom?.[MESSAGE_SUB_AGENTS_KEY];
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : undefined;
}

/** Welcome card id a user message was sent from, or undefined for a typed message. */
export function getMessageSuggestionId(
  message: MetadataCarrier | undefined | null,
): string | undefined {
  const id = message?.metadata?.custom?.[MESSAGE_SUGGESTION_ID_KEY];
  return typeof id === "string" && id ? id : undefined;
}
